import { i18n } from '@i18n/translations';
import { Lottie } from 'lottie-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import loaderAnimation from '@assets/images/neo-app-loader/neo-app-loader.json';
import loaderSvg from '@assets/images/neo-app-loader/neo-app-loader.svg?url';

interface FullPageLoaderProps {
  /**
   * Optional subtitle shown BELOW the brand mark. Only render for
   * stages that genuinely inform the operator ("Validando estado
   * del contrato…"). Default is silent — the brand mark carries
   * the identity; unnecessary text noise breaks the calm.
   */
  subtitle?: string;
  /**
   * Whether to translate the subtitle key via i18n.
   * @default true
   */
  useTranslation?: boolean;
}

const REDUCE_MOTION_QUERY = '(prefers-reduced-motion: reduce)';

/**
 * Unified full-page loading identity. Renders the animated
 * Neo App brand mark (Lottie) as the ONLY hero motion; falls back
 * to the final-frame SVG when the operator opts into reduced
 * motion (WCAG 2.3.3 exemption for essential state motion still
 * respected — we hold the identity but drop the animation).
 *
 * The visual must match `apps/neocrm/index.html` #neocrm-splash
 * so refresh does not flash between two loader designs.
 */
export const FullPageLoader: React.FC<FullPageLoaderProps> = ({
  subtitle,
  useTranslation: shouldTranslate = true,
}) => {
  const { t } = useTranslation();
  const [reduceMotion, setReduceMotion] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia(REDUCE_MOTION_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(REDUCE_MOTION_QUERY);
    const listener = (event: MediaQueryListEvent) =>
      setReduceMotion(event.matches);
    mql.addEventListener('change', listener);
    return () => mql.removeEventListener('change', listener);
  }, []);

  const resolvedSubtitle = subtitle ?? i18n.COMMON.LOADING_DEFAULT;
  const displaySubtitle = shouldTranslate
    ? t(resolvedSubtitle)
    : resolvedSubtitle;

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#f7f8fc]">
      <div className="flex flex-col items-center">
        {/* The mark's SVG viewBox includes ~70px of bottom whitespace
            below the wordmark. `-mb-10` collapses the worst of it
            without crushing the dots against the wordmark. */}
        <div className="-mb-10 h-56 w-56">
          {reduceMotion ? (
            <img
              src={loaderSvg as unknown as string}
              alt="Neo App"
              width={224}
              height={224}
              className="h-56 w-56"
            />
          ) : (
            <Lottie
              src={loaderAnimation}
              loop
              autoplay
              aria-label="Neo App"
              className="h-56 w-56"
            />
          )}
        </div>
        <div className="mt-4 flex items-end gap-2" aria-hidden="true">
          <span className="neo-loader-dot h-2 w-2 rounded-full bg-[#4b4a8f]" />
          <span className="neo-loader-dot h-2 w-2 rounded-full bg-[#4b4a8f]" />
          <span className="neo-loader-dot h-2 w-2 rounded-full bg-[#4b4a8f]" />
        </div>
        <p className="mt-6 text-sm font-medium text-[#6e6e81]">
          {displaySubtitle}
        </p>
      </div>
    </div>
  );
};

/**
 * Pre-configured loader for account setup scenarios. Kept for
 * back-compat with `<AccountSetupLoader />` call sites — routes
 * through the same unified visual.
 */
export const AccountSetupLoader: React.FC = () => (
  <FullPageLoader subtitle={i18n.ONBOARDING.SETTING_UP_ACCOUNT} />
);

export default FullPageLoader;
